import { action, makeAutoObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { TFunction } from 'i18next';
import { setTree } from '30.quickConnect.Stores/helpers/setTree';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { TreeViewData } from '10.quickConnect.app/components/domain/Home/types';
import { TreeDataItem } from '30.quickConnect.Stores/helpers/type';
import { normalizeFormId } from '30.quickConnect.Stores/RootStore/FormStore/favorites';

class ViewStore<T extends TreeDataItem> {
  // Tag
  private static readonly TAG = '30.quickConnect.Stores/RootStore/ViewStore/index.ts';

  logger: CustomLogger;

  items: T[] | undefined;

  itemsTree: TreeViewData<T>[] | undefined;

  treeNodeKeys: string[] = [];

  expandedNodes: string[] = [];

  // Identifiants (normalisés) des formulaires favoris, regroupés dans le dossier « Favoris » de l'arbre
  favoriteIds: string[] = [];

  filter = '';

  numberDisplayed = 0;

  constructor(logger: CustomLogger, storageKey: string) {
    this.logger = logger;
    makeAutoObservable(
      this,
      {
        filter: observable,
        numberDisplayed: observable,
        items: observable,
        itemsTree: observable,
        treeNodeKeys: observable,
        expandedNodes: observable,

        setFilter: action,
        setItems: action.bound,
        setItemsTree: action,
        setExpandedNodes: action,
        removeItem: action.bound,
      },
      { autoBind: true },
    );

    void makePersistable(this, {
      name: storageKey,
      properties: ['items', 'filter'],
      storage: window.localStorage,
    });
  }

  setItems = async (items: T[]) => {
    this.items = items;
  };

  reset = () => {
    this.items = [];
    this.itemsTree = undefined;
    this.treeNodeKeys = [];
    this.expandedNodes = [];
    this.numberDisplayed = 0;
  };

  setExpandedNodes = (expandedNodes: string[]) => (this.expandedNodes = expandedNodes);

  setFilter = (filter: string): void => {
    // On ignore les accents est les majuscules
    this.filter = filter
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  filterThis = (items: T[], getName: (item: T) => string) => this.filterDeclaration(items, getName);

  setItemsTree = (
    getName: (item: T) => string,
    t: TFunction,
    forInbox: boolean,
    favoriteIds: string[] = this.favoriteIds,
  ) => {
    this.favoriteIds = favoriteIds;
    // Favori reconnu par l'identifiant du formulaire (le nom peut être traduit, renommé ou partagé)
    const isFavorite = (item: T) => favoriteIds.includes(normalizeFormId(item.id));

    // 1. Filtrer les items pour exclure ceux qui sont dans les favoris
    let itemsToUse = [...(this.items ?? [])];

    // 2. Ajout des favoris avec folderPath modifié et exclusion des originaux
    if (favoriteIds.length > 0 && this.items) {
      const favoriteFolderName = t('qcapp_favorite_forms', { ns: 'forms' });

      // Créer les copies pour les favoris
      const favoriteCopies = this.items.filter(isFavorite).map((item) => ({
        ...item,
        folderPath: `${favoriteFolderName}\\${(item as any).folderPath}`,
      }));

      // Exclure les items originaux qui sont dans les favoris
      itemsToUse = itemsToUse.filter((item) => !isFavorite(item));

      // 3. On injecte uniquement les favoris (les originaux sont exclus)
      itemsToUse = [...favoriteCopies, ...itemsToUse];
    }

    // 4. Génération de l’arbre
    const [tree, treeNodeKeys] = setTree(itemsToUse, this.filterDeclaration, t, forInbox, getName);

    // 5. Tri pour mettre "Favoris" en haut
    const favoriteFolderName = t('qcapp_favorite_forms', { ns: 'forms' });
    tree.sort((a, b) => {
      if (a.name.startsWith(favoriteFolderName)) return -1;
      if (b.name.startsWith(favoriteFolderName)) return 1;
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });

    this.itemsTree = tree;
    this.treeNodeKeys = treeNodeKeys;
  };

  removeItem(itemId: string): void {
    if (this.items) {
      const index = this.items.findIndex((i) => i.id === itemId);
      if (index !== -1) {
        this.items.splice(index, 1);
      }
    }
  }

  removeActivityId(activityId: string): void {
    if (!this.items) return;

    const index = this.items.findIndex((i: T) => ('activityId' in i ? i.activityId === activityId : false));

    if (index !== -1) this.items.splice(index, 1);
  }

  private filterDeclaration(items: T[], getName: (item: T) => string): T[] {
    const filtered = this.filter
      ? // On ignore les accents est les majuscules
        items.filter((f) =>
          getName(f)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .includes(this.filter),
        )
      : items;
    this.numberDisplayed = filtered.length;
    return filtered;
  }
}
export default ViewStore;
